import React, { useContext, useState, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { DataGridPro, GRID_STRING_COL_DEF } from '@mui/x-data-grid-pro';
import CheckIcon from '@mui/icons-material/Check';
import WarningTriangle from '../../../components/WarningTriangle';
import { Button, Typography, Backdrop } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

import { GBENSContext } from '../GBENSContainer';
import {
  SubmitModal,
  DeleteConsignmentModal,
  DuplicateModal,
  DeleteShipmentModal,
  CannotDeleteShipmentModal,
} from '../Modals/MenuBarModals';

import { SubmitENS } from '../../../api/GBENSDec';

function StatusCell({ value }) {
  if (!value?.errors) return <CheckIcon sx={{ color: 'green' }} />;
  return (
    <WarningTriangle
      message={value?.message}
      errors={Object.keys(value?.errors ?? {})
        .filter((k) => k !== 'lines' && k !== 'id')
        .map((k) => value?.errors[k])}
    />
  );
}

StatusCell.propTypes = {
  value: PropTypes.object,
};

const statusColumnType = {
  ...GRID_STRING_COL_DEF,
  type: 'custom',
  resizable: false,
  filterable: false,
  sortable: false,
  editable: false,
  groupable: false,
  display: 'flex',
  renderCell: (params) => <StatusCell {...params} />,
};

const ReviewAndSubmit = () => {
  const [clientJobNumber, setClientJobNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const {
    headerId,
    deleteConsignmentModalOpen,
    setDeleteConsignmentModalOpen,
    duplicateModalOpen,
    setDuplicateModalOpen,
    setDeleteShipmentModalOpen,
    deleteShipmentModalOpen,
    cannotDeleteShipmentModalOpen,
    setCannotDeleteShipmentModalOpen,
    submitModalOpen,
    setSubmitModalOpen,
    data,
    errors,
    allValid,
    isReadOnly,
  } = useContext(GBENSContext);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const rtrnValue = await SubmitENS(headerId);
      setClientJobNumber(rtrnValue?.clientJobNumber);
      setSubmitModalOpen(true);
      // navigate('/Dashboard');
    } catch (error) {
      console.error('Error submitting ENS:', error);
      setIsSubmitting(false);
    }
  };

  const shipmentStatus = useMemo(() => {
    if (!data?.TransportAndJourney?.statusDescription) return 'Valid';
    if (data?.TransportAndJourney?.statusDescription?.toUpperCase?.() === 'DRAFT' && allValid) return 'Valid';
    return data?.TransportAndJourney?.statusDescription;
  }, [data?.TransportAndJourney?.statusDescription]);

  //---Header Table-----
  const rowsHeader = [
    {
      id: 1,
      reference: data?.TransportAndJourney?.jobReference,
      vehicleID: data?.TransportAndJourney?.Vehicle?.vehicleID,
      departureDate: data.TransportAndJourney?.departureDate?.format?.('DD/MM/YYYY'),
      arrivalDate: data.TransportAndJourney?.arrivalDateTime?.format?.('DD/MM/YYYY'),
      noConsignments: data.Consignments?.length,
      status: shipmentStatus,
    },
  ];
  const columnsHeader = [
    { field: 'reference', headerName: 'Reference', flex: 1, headerClassName: 'theme--header' },
    { field: 'vehicleID', headerName: 'Vehicle Identity', flex: 1, headerClassName: 'theme--header' },
    { field: 'departureDate', headerName: 'Departure Date', headerClassName: 'theme--header', flex: 1 },
    { field: 'arrivalDate', headerName: 'Arrival Date', headerClassName: 'theme--header', flex: 1 },
    { field: 'noConsignments', headerName: 'Number of Consignments', headerClassName: 'theme--header', flex: 1 },
    { field: 'status', headerName: 'Status', headerClassName: 'theme--header', flex: 1 },
  ];

  //---Consignment Table-----
  const rowsConsignments = [...(data?.Consignments ?? [])].map((c) => ({
    id: c?.id,
    reference: c?.consignmentReference,
    consignor: c?.Consignor?.companyName,
    consignee: c?.Consignee?.companyName,
    MRN: c?.statusDescription?.toUpperCase() === 'ACCEPTED' ? c.mrn : 'N/a',
    status: {
      message: 'There are errors with this consignment.',
      errors:
        errors?.Consignments?.find?.(
          (e) => e.id === c.id && Object.keys(e).filter((k) => k !== 'id' && k !== 'lines').length > 0
        ) ?? null,
    },
  }));

  const columnsConsignments = [
    { field: 'status', headerName: 'Status', headerClassName: 'theme--header', flex: 1, ...statusColumnType },
    { field: 'reference', headerName: 'Reference', headerClassName: 'theme--header', flex: 1 },
    { field: 'consignor', headerName: 'Consignor', headerClassName: 'theme--header', flex: 1 },
    { field: 'consignee', headerName: 'Consignee', headerClassName: 'theme--header', flex: 1 },
    { field: 'MRN', headerName: 'MRN', headerClassName: 'theme--header', flex: 1 },
  ];

  if (!allValid)
    return (
      <>
        <SubmitModal
          clientJobNumber={clientJobNumber}
          open={submitModalOpen}
          onClose={() => setSubmitModalOpen(false)}
        />
        <DeleteConsignmentModal
          open={deleteConsignmentModalOpen}
          onClose={() => setDeleteConsignmentModalOpen(false)}
        />
        <DuplicateModal open={duplicateModalOpen} onClose={() => setDuplicateModalOpen(false)} headerId={headerId} />
        <DeleteShipmentModal
          open={deleteShipmentModalOpen}
          onClose={() => setDeleteShipmentModalOpen(false)}
          headerId={headerId}
          reference={rowsHeader[0]?.reference}
        />
        <CannotDeleteShipmentModal
          open={cannotDeleteShipmentModalOpen}
          onClose={() => setCannotDeleteShipmentModalOpen(false)}
        />
        <div>
          <Typography id="modal-modal-title" variant="h6" component="h6" style={{ marginLeft: 0, marginBottom: 20 }}>
            Your ENS data is incomplete.
          </Typography>
          <Typography id="modal-modal-title" variant="p" component="p" style={{ marginLeft: 0, marginBottom: 20 }}>
            There is invalid or missing data in this declaration.
          </Typography>
          <Typography id="modal-modal-title" variant="p" component="p" style={{ marginLeft: 0, marginBottom: 20 }}>
            Please review the steps and update the highlighted fields.
          </Typography>
        </div>

        <div style={{ backgroundColor: '#F6F6F6', padding: 30 }}>
          <DataGridPro
            hideFooter
            rows={rowsHeader}
            columns={columnsHeader}
            style={{ marginBottom: 30, background: '#fff' }}
          />

          <DataGridPro
            initialState={{
              pagination: {
                paginationModel: { pageSize: 15 },
              },
            }}
            style={{ background: '#fff' }}
            pageSizeOptions={[15, 25, 50, 100]}
            pagination
            rows={rowsConsignments}
            columns={columnsConsignments}
          />
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            paddingBottom: 30,
            paddingLeft: 30,
            paddingRight: 30,
          }}
        >
          <div></div>
        </div>
      </>
    );

  return (
    <>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isSubmitting}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <SubmitModal open={submitModalOpen} onClose={() => setSubmitModalOpen(false)} />
      <DuplicateModal open={duplicateModalOpen} onClose={() => setDuplicateModalOpen(false)} headerId={headerId} />
      <DeleteConsignmentModal open={deleteConsignmentModalOpen} onClose={() => setDeleteConsignmentModalOpen(false)} />
      <DeleteShipmentModal
        open={deleteShipmentModalOpen}
        onClose={() => setDeleteShipmentModalOpen(false)}
        headerId={headerId}
        reference={rowsHeader[0]?.reference}
      />
      <CannotDeleteShipmentModal
        open={cannotDeleteShipmentModalOpen}
        onClose={() => setCannotDeleteShipmentModalOpen(false)}
      />
      <div style={{ marginBottom: 30 }}>
        <Typography id="modal-modal-title" variant="h6" component="h6" style={{ marginLeft: 0, marginBottom: 20 }}>
          Your ENS data is valid and can be <u>submitted</u> to HMRC.
        </Typography>
        <Typography id="modal-modal-title" variant="p" component="p" style={{ marginLeft: 0, marginBottom: 20 }}>
          Each consignment will be submitted seperately. All consignments must be <b>ACCEPTED</b> prior to the goods
          being loaded.
        </Typography>
        <Typography id="modal-modal-title" variant="p" component="p" style={{ marginLeft: 0, marginBottom: 20 }}>
          <b>WARNING:</b> HMRC may flag the goods as &apos;Do Not Load&apos;. If this occurs, please contact Border
          Force for the next steps.
        </Typography>
        <Typography id="modal-modal-title" variant="p" component="p" style={{ marginLeft: 0, marginBottom: 20 }}>
          During peak periods it may take up to 10 minutes to receive responses from HMRC. Please refresh your
          dashboard.
        </Typography>

        <Typography id="modal-modal-title" variant="p" component="p" style={{ marginLeft: 0, marginBottom: 20 }}>
          <b>Submission and Response time to HMRC</b>
        </Typography>
        <ul style={{ marginLeft: 0 }}>
          <li>
            After submitting an ENS declaration, it may take up to 5 minutes to receive a response. During this time,
            the status will appear as <b>In Progress</b>.
          </li>
          <li>
            For further information please check the kb article:{' '}
            <a
              href="https://www.eori.uk/customs-knowledge/how-to-submit-an-ens-declaration"
              target="_blank"
              rel="noopener noreferrer"
              style={{ cursor: 'pointer', color: 'rgb(0 47 129)' }}
            >
              Submitting ENS Declarations: Process, Response Times, and Troubleshooting
            </a>
          </li>
        </ul>
      </div>
      <div style={{ backgroundColor: '#F6F6F6', padding: 30, marginBottom: 30 }}>
        <DataGridPro
          hideFooter
          rows={rowsHeader}
          columns={columnsHeader}
          style={{ marginBottom: 30, backgroundColor: '#ffffff' }}
        />

        <DataGridPro
          pagination
          initialState={{
            pagination: {
              paginationModel: { pageSize: 50 },
            },
          }}
          pageSizeOptions={[20, 50, 100]}
          rows={rowsConsignments}
          columns={columnsConsignments}
          style={{ backgroundColor: '#ffffff' }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingBottom: 30,
          paddingLeft: 30,
          paddingRight: 30,
        }}
      >
        <div></div>
        <Button variant="outlined" onClick={handleSubmit} disabled={isReadOnly}>
          Submit
        </Button>
      </div>
    </>
  );
};

export default ReviewAndSubmit;
